import React, { forwardRef, useCallback, useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core/styles'
import MaterialTable from 'material-table'
import { ReactComponent as Swipe2GoLogo } from 'assets/swipe2Go.svg'

import RequestButton from 'components/RequestButton'

import {
  BOOKING_PROVIDER_FAIRTIQ,
  BOOKING_PROVIDER_FAIRTIQ_ORTENAU_MOBIL,
} from 'util/constants'

import {
  getDisplayNameForMobilityProvider,
} from 'util/booking'

import {
  AddBox,
  ArrowDownward,
  Check,
  ChevronLeft,
  ChevronRight,
  Clear,
  DeleteOutline,
  Edit,
  FilterList,
  FirstPage,
  LastPage,
  Remove,
  SaveAlt,
  Search,
  ViewColumn,
} from '@material-ui/icons'

import AwesomeDebouncePromise from 'awesome-debounce-promise'
import logger from 'util/logger'
import useInitClient from 'hooks/useInitClient'
import RefundDialog from 'components/Dialog/RefundDialog'
import { useParams } from 'react-router-dom'

import {
  Avatar,
  Checkbox,
  FormControl,
  Grid,
  Input,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Tab,
  Tabs,
  Toolbar,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@material-ui/core'

const formatTime = timeString => {
  if (!timeString) {
    return '-' // Default value for invalid or missing date
  }
  try {
    const date = new Date(timeString)
    if (isNaN(date.getTime())) {
      throw new Error('Invalid date')
    }
    return Intl.DateTimeFormat('de-DE', {
      year: 'numeric',
      day: '2-digit',
      month: '2-digit',
      hour: 'numeric',
      minute: 'numeric',
    }).format(date)
  } catch (error) {
    return '-' // Default value
  }
}

const FAIRTIQ_PAYMENT_STATES = {
  Cleared: 'Abgerechnet',
  Waiting: 'Ausstehend',
  Canceled: 'Storniert',
  Accepted: 'Akzeptiert',
  Refunded: 'Erstattet',
  Failed: 'Fehlgeschlagen',
  Refund: 'Gutschrift',
  Reservation: 'Vormerkung',
  OriginalPurchase: 'Ursprünglicher Kauf',
}

const filterOptions = Object.entries(FAIRTIQ_PAYMENT_STATES).map(([value, label]) => ({
  value,
  label: label ?? value,
}))

const FareSalesTable = (props) => {
  const {
    refundFareSale,
  } = props

  const classes = useStyles()
  const initClient = useInitClient()
  const { userId } = useParams()

  const [tabFilter, setTabFilter] = useState(null)
  // filter needs to be an array to work with material ui select implementation
  const [filter, setFilter] = useState()

  const [mobilityProviders, setMobilityProviders] = useState([])

  useEffect(() => {
    initClient.mobilityProviders.getAll().then((result) => {
      setMobilityProviders(result.body.Result.filter((i) => i.MobilityProviderName === 'Fairtiq'))
    })
  }, [initClient.mobilityProviders])

  const refundFareSaleCallback = useCallback(
    (event, rowData) => {
      if (rowData.MobilityProvider === BOOKING_PROVIDER_FAIRTIQ_ORTENAU_MOBIL || rowData.MobilityProvider === BOOKING_PROVIDER_FAIRTIQ) {
        return refundFareSale(rowData.PaymentNumber, rowData.CustomerAccountId, rowData.Amount, rowData.PaymentProviderReference, rowData.PaymentReference)
          .then(() => {
            tableRef.current.onQueryChange()
          })
      }
    },
    [refundFareSale],
  )

  const tableIcons = {
    Add: forwardRef((props, ref) => <AddBox {...props} ref={ref} />),
    Check: forwardRef((props, ref) => <Check {...props} ref={ref} />),
    Clear: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
    Delete: forwardRef((props, ref) => <DeleteOutline {...props} ref={ref} />),
    DetailPanel: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
    Edit: forwardRef((props, ref) => <Edit {...props} ref={ref} />),
    Export: forwardRef((props, ref) => <SaveAlt {...props} ref={ref} />),
    Filter: forwardRef((props, ref) => <FilterList {...props} ref={ref} />),
    FirstPage: forwardRef((props, ref) => <FirstPage {...props} ref={ref} />),
    LastPage: forwardRef((props, ref) => <LastPage {...props} ref={ref} />),
    NextPage: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
    PreviousPage: forwardRef((props, ref) => <ChevronLeft {...props} ref={ref} />),
    ResetSearch: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
    Search: forwardRef((props, ref) => <Search {...props} ref={ref} />),
    SortArrow: forwardRef((props, ref) => <ArrowDownward {...props} ref={ref} />),
    ThirdStateCheck: forwardRef((props, ref) => <Remove {...props} ref={ref} />),
    ViewColumn: forwardRef((props, ref) => <ViewColumn {...props} ref={ref} />),
  }

  const renderColumn = (caption, info) => {
    return (
      <>
        <Typography variant='caption' noWrap className={classes.caption}>{caption}</Typography>
        <Typography variant='body2' noWrap>{info || ''}</Typography>
      </>
    )
  }

  const renderColumnFairtiqHead = (rowData, column) => {
    const fairtiqHead = [
      { caption: 'Zahlungsnummer', info: rowData?.FareSaleId },
      { caption: 'Rechnungsnummer', info: rowData?.ExternalInvoiceReference },
      { caption: 'Abrechnungsdatum', info: formatTime(rowData?.FinalizationDate) },
      { caption: 'Status', info: FAIRTIQ_PAYMENT_STATES[rowData?.State] || rowData?.State },
      {
        caption: 'Preis',
        info: Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(rowData?.FinalAmount /
            100),
      },
    ]

    return renderColumn(fairtiqHead[column]?.caption, fairtiqHead[column]?.info)
  }

  const renderTableColumn = (rowData, column) => {
    switch (rowData.MobilityProvider) {
      case BOOKING_PROVIDER_FAIRTIQ :
      case BOOKING_PROVIDER_FAIRTIQ_ORTENAU_MOBIL :
      {
        return renderColumnFairtiqHead(rowData, column)
      }
      default: {
        return null
      }
    }
  }

  const renderPaymentChildColumn = (payment, column) => {
    const paymentData = [
      { caption: 'Zahlungs ID', info: payment?.PaymentId || '-' },
      { caption: 'Ausführungszeit', info: formatTime(payment?.ExecutionTime) || '-' },
      { caption: 'Fairtiq-Zahlungsreferenz', info: payment?.Description },
      { caption: 'Zahlungsstatus', info: FAIRTIQ_PAYMENT_STATES[payment?.PaymentState] || payment?.PaymentState },
      { caption: 'Zahlungstyp', info: FAIRTIQ_PAYMENT_STATES[payment?.PaymentType] || '-' },
      { caption: 'Ursprüngliche Zahlung', info: payment?.OriginalPayment || '-' },
      { caption: 'Zahlungsmethode', info: payment?.PaymentMethod || '-' },
      {
        caption: 'Vorgemerkter Betrag',
        info: Intl.NumberFormat('de-DE', {
          style: 'currency',
          currency: 'EUR',
        }).format(
          (payment?.ReservedAmount ?? 0) / 100
        ) || '-',
      },
      {
        caption: 'Betrag',
        info: Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(
          (payment?.Amount ?? 0) / 100
        ),
      },
      { caption: 'Referenz', info: payment?.ReceivedReference || '-' },
    ]

    return renderColumn(paymentData[column]?.caption, paymentData[column]?.info)
  }

  const tableRef = useRef()

  const FirstColumn = ({ booking }) => {
    return <FirstColumnManyPaymentsCell booking={booking} />
  }

  FirstColumn.propTypes = {
    booking: PropTypes.shape({
      Payments: PropTypes.array,
      MobilityProvider: PropTypes.string,
    }),
  }

  return (
    <Grid
      container
      className={classes.grid}
    >
      <Grid
        item
        className={classes.table}
      >
        <MaterialTable
          tableRef={tableRef}
          icons={tableIcons}
          components={{
            Action: RefundAction,
            Toolbar: props => (
              <Toolbar disableGutters>
                <MobilityProviderTabs
                  mobilityProviders={mobilityProviders}
                  onChange={(event, tabFilter) => {
                    setTabFilter(tabFilter)
                    // eslint-disable-next-line react/prop-types
                    props.onSearchChanged()
                  }}
                  value={tabFilter}
                />
                {/* https://v4.mui.com/components/selects/#multiple-select */}
                <FormControl className={classes.formControl}>
                  <InputLabel
                    id='filter-label'
                    // XXX always shrink to match search field
                    shrink
                  >
                    Filter
                  </InputLabel>
                  <Select
                    input={<Input />}
                    labelId='filter-label'
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 48 * 4.5 + 8,
                          width: 250,
                        },
                      },
                    }}
                    onChange={(e) => {
                      setFilter(e?.target?.value ?? null)
                      // eslint-disable-next-line react/prop-types
                      props.onSearchChanged()
                    }}
                    renderValue={(selected) => selected?.label ?? '-'}
                    // required to render something if nothing is selected
                    displayEmpty
                    value={filter}
                  >
                    {
                      filterOptions.map((fo) => (
                        <MenuItem
                          key={fo.parameter + fo.value}
                          value={fo}
                        >
                          <Checkbox checked={filter === fo} />
                          <ListItemText primary={fo.label} />
                        </MenuItem>
                      ))
                    }
                  </Select>
                </FormControl>
              </Toolbar>
            ),
          }}
          detailPanel={[
            rowData => ({
              disabled: !rowData?.Payments,
              icon: () => (
                <ChevronRight
                  className={(!rowData?.Payments) ? classes.displayNone : ''}
                />
              ),
              render: rowData => {
                if (!rowData?.Payments) {
                  return false
                }

                return (
                  <Table>
                    <TableBody>
                      {rowData.Payments?.sort((a, b) => new Date(b?.ExecutionTime || 0) - new Date(a?.ExecutionTime || 0))?.map((payment, index) => (
                        <TableRow key={payment.PaymentNumber || payment.FareSalePaymentId || index}>
                          <TableCell style={{ width: 55 }} />
                          <TableCell style={{ width: 300 }}>
                            <FirstColumnCell
                              mobilityProvider={rowData.MobilityProvider}
                              caption='Swipe2Go'
                              info={'Teilzahlung ' + (index + 1)}
                            />
                          </TableCell>
                          <TableCell style={{ width: 175 }}>
                            {renderPaymentChildColumn(payment, 0)}
                          </TableCell>
                          <TableCell style={{ width: 175 }}>
                            {renderPaymentChildColumn(payment, 1)}
                          </TableCell>
                          <TableCell style={{ width: 175 }}>
                            {renderPaymentChildColumn(payment, 2)}
                          </TableCell>
                          <TableCell style={{ width: 175 }}>
                            {renderPaymentChildColumn(payment, 3)}
                          </TableCell>
                          <TableCell style={{ width: 175 }}>
                            {renderPaymentChildColumn(payment, 4)}
                          </TableCell>
                          <TableCell style={{ width: 175 }}>
                            {renderPaymentChildColumn(payment, 5)}
                          </TableCell>
                          <TableCell style={{ width: 175 }}>
                            {renderPaymentChildColumn(payment, 6)}
                          </TableCell>
                          <TableCell style={{ width: 175 }}>
                            {renderPaymentChildColumn(payment, 7)}
                          </TableCell>
                          <TableCell style={{ width: 175 }}>
                            {renderPaymentChildColumn(payment, 8)}
                          </TableCell>
                          <TableCell style={{ width: 175 }}>
                            {renderPaymentChildColumn(payment, 9)}
                          </TableCell>
                          <TableCell style={{ width: 175 }}>
                            {renderPaymentChildColumn(payment, 10)}
                          </TableCell>

                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )
              },
            }),
          ]}
          options={{
            actionsColumnIndex: -1,
            draggable: false,
            emptyRowsWhenPaging: false,
            header: false,
            pageSize: 10,
            pageSizeOptions: [10, 50, 100, 250],
            search: true,
            showEmptyDataSourceMessage: true,
            showFirstLastPageButtons: true,
            showTitle: false,
            toolbar: true,
          }}
          columns={[
            {
              field: 'MobilityProvider',
              render: rowData => <FirstColumn booking={rowData} />,
              // XXX fixed width to approximately match nested table in detail panel
              cellStyle: {
                width: 300,
              },
            },
            {
              field: 'PaymentNumber',
              render: rowData => renderTableColumn(rowData, 0),
            },
            {
              field: 'Amount',
              render: rowData => renderTableColumn(rowData, 1),
            },
            {
              field: 'PaymentProviderReference',
              render: rowData => renderTableColumn(rowData, 2),
            },
            {
              field: 'PaymentReference',
              render: rowData => renderTableColumn(rowData, 3),
            },
            {
              field: 'State',
              render: rowData => renderTableColumn(rowData, 4),
            },
            {
              field: 'ExecutionTime',
              render: rowData => renderTableColumn(rowData, 5),
            },
          ]}
          data={query => {
            return new Promise((resolve, reject) => {
              return _debouncedSearchFunction(
                initClient,
                userId,
                filter,
                tabFilter,
                query,
                resolve)
            })
          }}
          actions={[
            {
              icon: 'refundButton',
              tooltip: 'Erstatten',
              onClick: refundFareSaleCallback,
            },
          ]}
        />
      </Grid>
    </Grid>
  )
}

FareSalesTable.propTypes = {
  refundFareSale: PropTypes.func.isRequired,
}

const FirstColumnCell = ({ mobilityProvider, caption, info }) => {
  const classes = useStyles()
  const tableData = {
    Fairtiq: {
      backgroundColor: '#6476b8',
      icon: () => <Swipe2GoLogo widht={24} height={24} />,
      caption: 'Swipe2Go',
      info: '',
    },
    FairtiqOrtenauMobil: {
      backgroundColor: '#6476b8',
      icon: () => <Swipe2GoLogo widht={24} height={24} />,
      caption: 'Swipe2Go',
      info: '',
    },
  }

  return (
    <Grid container alignItems='center' wrap='nowrap'>
      <Grid item>
        <Avatar
          classes={{ root: classes.avatar }}
          style={{
            backgroundColor: tableData[mobilityProvider]?.backgroundColor,
          }}
        >
          {tableData[mobilityProvider]?.icon?.()}
        </Avatar>
      </Grid>
      <Grid item className={classes.pl1}>
        <Typography variant='body1' noWrap>
          {caption ?? tableData[mobilityProvider]?.caption}
        </Typography>
        <Typography variant='body2' noWrap>
          {info ?? tableData[mobilityProvider]?.info}
        </Typography>
      </Grid>
    </Grid>
  )
}

FirstColumnCell.propTypes = {
  mobilityProvider: PropTypes.string.isRequired,
  caption: PropTypes.string,
  info: PropTypes.string,
}

const FirstColumnManyBookingItemsCell = ({ booking }) => {
  const mobilityProvider = booking?.MobilityProvider
  const caption = booking?.BookingItems?.length + ' Tickets'
  const info = Array.from(
    new Set(
      booking?.BookingItems?.map(a => getDisplayNameForMobilityProvider(a.MobilityProvider))
    )
  ).join(', ')

  return <FirstColumnCell mobilityProvider={mobilityProvider} caption={caption} info={info} />
}

FirstColumnManyBookingItemsCell.propTypes = {
  booking: PropTypes.object,
}

const MobilityProviderTabs = ({
  mobilityProviders,
  onChange,
  value,
}) => {
  const classes = useStyles()

  return (
    <Tabs
      className={classes.tabs}
      indicatorColor='primary'
      onChange={onChange}
      value={value}
    >
      <Tab value={null} label='Alle' />
      {
        mobilityProviders.map((provider) => {
          switch (provider.MobilityProviderName) {
            case 'Fairtiq':
              return <Tab value={provider.MobilityProviderName} label='Swipe2Go' key={provider.MobilityProviderName} />
            default:
              return <Tab value={provider.MobilityProviderName} label={provider.MobilityProviderName} key={provider.MobilityProviderName} />
          }
        }
        )
      }
    </Tabs>
  )
}

MobilityProviderTabs.propTypes = {
  mobilityProviders: PropTypes.array,
  onChange: PropTypes.func,
  value: PropTypes.any,
}

MobilityProviderTabs.defaultProps = {
  mobilityProviders: [],
}

const FirstColumnManyPaymentsCell = ({ booking }) => {
  const mobilityProvider = booking?.MobilityProvider

  return (
    <FirstColumnCell
      mobilityProvider={mobilityProvider}
      caption={`${booking?.Payments?.length || 0} Zahlungen`}
      info='Zum Anzeigen ausklappen'
    />
  )
}

FirstColumnManyPaymentsCell.propTypes = {
  booking: PropTypes.object,
}

const RefundAction = (props) => {
  const {
    action,
    data,
  } = props

  const {
    MobilityProvider,
    State,
  } = data

  const classes = useStyles()
  const [open, setRefundDialogOpen] = useState(false)

  const hasBeenRefunded = State === 6
  const isRefundable = State === 1

  if (hasBeenRefunded) {
    return (
      <Grid className={classes.actionColumnWidth}>
        <RequestButton
          color='primary'
          className={classes.mr1}
          disabled
          fullWidth
          variants='text'
        >
          Erstattet
        </RequestButton>
      </Grid>
    )
  } else if (isRefundable) {
    return (
      <Grid className={classes.actionColumnWidth}>
        <RequestButton
          color='primary'
          className={classes.mr1}
          fullWidth
          variant='outlined'
          onClick={() => { setRefundDialogOpen(true) }}
        >
          Erstatten
        </RequestButton>
        <RefundDialog
          open={open}
          onClose={() => { setRefundDialogOpen(false) }}
          isCommentAllowed={false}
          refundingTicket={(event) => action.onClick(event, data)?.then(() => { setRefundDialogOpen(false) })}
          mobilityProvider={MobilityProvider}
        />
      </Grid>
    )
  } else {
    return null
  }
}

RefundAction.propTypes = {
  data: PropTypes.object,
}

const useStyles = makeStyles(theme => ({
  avatar: {
    width: 50,
    height: 50,
    color: 'white',
  },
  caption: {
    color: theme.raumo.palette.typography.textColorMediumEmphasisDark,
  },
  mr1: {
    marginRight: theme.spacing(1),
  },
  pl1: {
    paddingLeft: theme.spacing(1),
  },
  tabs: {
    margin: theme.spacing(0, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    // this pushes filter and search to the right
    flexGrow: 1,
  },
  table: {
    width: '100%',
  },
  actionColumnWidth: {
    width: '150px',
  },
  displayNone: {
    display: 'none',
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
    maxWidth: 300,
  },
}))

const _searchFunction = (
  initClient,
  userId,
  filter,
  tabFilter,
  query,
  resolve) => {
  return initClient.fareSales.getCustomerFareSales({
    CustomerAccountId: userId,
    Sort: 'FinalizationDate:desc',
    MobilityProvider: tabFilter,
    PaymentState: filter?.value.toString(),
    skip: query.page * query.pageSize,
    take: query.pageSize,
  })
    .then(response => {
      const { Result: bookings } = response.obj
      // This resolve with given structure is needed by material-table
      resolve({
        data: bookings || [],
        page: query.page,
        totalCount: response.obj.TotalCount,
      })

      return response
    },
    error => {
      logger.error('Error while fetching data', error)
    })
}

const _debouncedSearchFunction = AwesomeDebouncePromise(_searchFunction, 500)

export default FareSalesTable
